import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { IconBuilding, IconChevronDown, IconCheck, IconView } from '../../icons';
import { useAuth } from '../../context/AuthContext';
import { TENANT_SCOPING_ENABLED } from '../../utils/tenantScope';
import type { AvailableTenant } from '../../types/tenantTypes';

// M-UX-3.2 (`plan-ux-3.2-advisor.md` §4.2): geteiltes Flag mit M-UX-3.1/3.2 —
// gatet, ob nach einem Tenant-Wechsel auf `/dashboard` navigiert wird (der
// `AdvisorHidden`-Guard dort berechnet dann das konditionale Landing-Ziel,
// Single Source E5). Flag off ⇒ kein `navigate` (heutiges Verhalten).
const UX_SHELL_ENABLED = process.env.REACT_APP_UX_SHELL_ENABLED === 'true';

/**
 * M11 W3 — Mandanten-Switch (INERT hinter REACT_APP_TENANT_SCOPING_ENABLED).
 *
 * Dient nach M11b-2 ausschließlich dem reinen Mandanten-Wechsel: sichtbar nur,
 * wenn das Flag on ist UND der User in mehr als einem Tenant ist. Bei Flag off
 * oder genau einem (bzw. keinem) Tenant rendert die Komponente nichts — ein Solo-
 * Owner sieht keinen sinnlosen 1-Eintrag-Switch mehr. Die Berater-Verwaltung
 * (Einladen + offene Einladungen) lebt jetzt auf der Konto-Seite
 * `/dashboard/konto/steuerberater` (AdvisorPortalPage), nicht mehr hier.
 *
 * Bei aktiver advisor-Rolle wird ein Read-only-Hinweis angezeigt. Das ist eine
 * rein kosmetische Schicht ÜBER den harten Sperren (Firestore-Rules + PHP-
 * `requireWrite`-Guard), nicht statt ihnen.
 */
const TenantSwitcher: React.FC = () => {
  const { t } = useTranslation('common');
  const { availableTenants, activeTenantId, activeRole, switchTenant, currentUser, sellerData } =
    useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Inert bei Flag off. Sonst nur sichtbar, wenn der User in mehr als einem Tenant
  // ist (echter Mandanten-Switch). Die frühere Owner-Ausnahme (Henne-Ei: erster
  // Invite aus dem Dropdown) entfällt mit M11b-2 — das Einladen passiert jetzt auf
  // der Konto-Seite, der Switcher braucht keine 1-Tenant-Sonderfälle mehr.
  if (!TENANT_SCOPING_ENABLED || availableTenants.length <= 1) {
    return null;
  }

  // M-ACF-6 (`architecture-advisor-cockpit-first.md` §3.5 / §7.9, Doppel-E-Mail-Fix,
  // UNGEFLAGGT): Für den EIGENEN Tenant (`tenantId === currentUser.uid`) nie mehr
  // die E-Mail als Label zeigen — bei leerem/whitespace Kanzleinamen (oder noch
  // nicht geladenem `sellerData`) auf `tenant.ownCompany` zurückfallen. Fremde
  // Tenants behalten `tenant.displayName` unverändert. Damit zeigen Switcher-Label
  // und Profilmenü (Personen-Identität) nie mehr denselben E-Mail-String. Der
  // jurist-geschützte `acceptLegalNotice`-Wortlaut ist nicht betroffen: er rendert
  // nur bei `activeRole==='advisor'` und interpoliert dann den FREMDEN Tenant.
  const labelForTenant = (tenant: AvailableTenant): string =>
    tenant.tenantId === currentUser?.uid
      ? sellerData?.name?.trim() || t('tenant.ownCompany')
      : tenant.displayName;

  const activeTenant = availableTenants.find((tenant) => tenant.tenantId === activeTenantId);
  const activeLabel = activeTenant ? labelForTenant(activeTenant) : t('tenant.ownCompany');
  const isAdvisor = activeRole === 'advisor';

  const handleSelect = (tenantId: string) => {
    switchTenant(tenantId);
    setOpen(false);
    // M-UX-3.2 (§4.2): nur bei Flag ON navigieren — `AdvisorHidden`
    // (`routes/dashboardChildren.tsx`) berechnet dann das konditionale
    // Landing-Ziel (Cockpit für Loop-B-Beta-Advisor, sonst Archiv/Owner-Index).
    if (UX_SHELL_ENABLED) {
      navigate('/dashboard');
    }
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center text-sm font-medium text-secondary hover:text-primary transition"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <span className="mr-1"><IconBuilding size={14} /></span>
        <span className="max-w-[10rem] truncate">{activeLabel}</span>
        {isAdvisor && (
          <span className="ml-2 inline-flex items-center rounded-full bg-[var(--tone-warning-bg)] px-2 py-0.5 text-xs text-[var(--tone-warning-fg)]">
            <span className="mr-1"><IconView size={14} /></span>
            {t('tenant.readOnlyBadge')}
          </span>
        )}
        <span className="ml-1 h-3 w-3"><IconChevronDown size={12} /></span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-surface-raised ring-1 ring-black ring-opacity-5 z-50">
          <div className="py-1" role="menu" aria-orientation="vertical">
            <div className="px-4 py-1 text-xs uppercase tracking-wide text-faint">
              {t('tenant.switchLabel')}
            </div>
            {availableTenants.map((tenant) => {
              const isActive = tenant.tenantId === activeTenantId;
              return (
                <button
                  key={tenant.tenantId}
                  type="button"
                  onClick={() => handleSelect(tenant.tenantId)}
                  className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm ${
                    isActive ? 'bg-indigo-600 text-white' : 'text-secondary hover:bg-surface-inset hover:text-primary'
                  }`}
                  role="menuitem"
                >
                  <span className="flex items-center min-w-0">
                    <span className="mr-2 shrink-0">
                      {tenant.role === 'advisor' ? <IconView size={14} /> : <IconBuilding size={14} />}
                    </span>
                    <span className="truncate">{labelForTenant(tenant)}</span>
                  </span>
                  {isActive && <span className="ml-2 shrink-0"><IconCheck size={14} /></span>}
                </button>
              );
            })}

            {isAdvisor && (
              <>
                <p className="px-4 py-2 text-xs text-[var(--tone-warning-fg)]">{t('tenant.readOnlyNotice')}</p>
                {/* Berater-Annahme-Hinweis (Jurist-Wortlaut, Daniel-freigegeben, M11 W4).
                    §-Verweise StBerG/StGB bleiben in allen Sprachen erhalten. */}
                <p className="px-4 pb-2 text-[11px] leading-relaxed text-faint">
                  {t('tenant.acceptLegalNotice', { mandant: activeLabel })}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantSwitcher;
